"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.bondDaysToIsoDays = exports.convertTime24ToAmPm = exports.convertTimestampFromTimezoneToTimezone = exports.convertLocalTimestampToUtc = exports.convertUtcTimestampToLocalTime = exports.convertTimestampFromTimezoneToUtc = exports.convertUtcTimestampToTimezone = void 0;
const dayjs_1 = __importDefault(require("dayjs"));
const utc_1 = __importDefault(require("dayjs/plugin/utc"));
const timezone_1 = __importDefault(require("dayjs/plugin/timezone"));
const formatter_1 = require("../formatter/formatter");
const parser_1 = require("../parser/parser");
const types_1 = require("../types");
dayjs_1.default.extend(utc_1.default);
dayjs_1.default.extend(timezone_1.default);
/**
 * Converts a UTC timestamp to a local time in the specified timezone and formats it as a string.
 *
 * @param {DateInput} input - The input date to convert, which is assumed to be in UTC.
 * @param {string} toTimezone - The timezone to which the timestamp should be converted.
 * @param {DateTimeFormats} [format=DateTimeFormats.LLLL] - The format to use for formatting the local time. Default is the LLLL.
 * @param {LanguageRegionCodes} [languageRegionCode=LanguageRegionCodes.EN_US] - The language and region code to use for localization. Default is the EN_US.
 * @returns {string} The formatted local time in the specified timezone.
 */
const convertUtcTimestampToTimezone = (input, toTimezone, format = types_1.DEFAULT_FORMAT, languageRegionCode = types_1.DEFAULT_LANGUAGE_REGION_CODE) => {
    // Set the input date in UTC
    const utcTime = (0, parser_1.parseUtc)(input);
    const convertedTime = utcTime.tz(toTimezone);
    // Format the local time using the selected format
    return (0, formatter_1.formatDateTime)(convertedTime, format, languageRegionCode);
};
exports.convertUtcTimestampToTimezone = convertUtcTimestampToTimezone;
/**
 * Converts a timestamp from a specific timezone to UTC and formats it as a string.
 *
 * @param {DateInput} input - The input date to convert from the specified timezone.
 * @param {string} fromTimezone - The timezone from which the timestamp should be converted to UTC.
 * @param {DateTimeFormats} [format=DateTimeFormats.LLLL] - The format to use for formatting the UTC time. Default is the LLLL.
 * @param {LanguageRegionCodes} [languageRegionCode=LanguageRegionCodes.EN_US] - The language and region code to use for localization. Default is the EN_US.
 * @returns {string} The formatted UTC time.
 */
const convertTimestampFromTimezoneToUtc = (input, fromTimezone, format = types_1.DEFAULT_FORMAT, languageRegionCode = types_1.DEFAULT_LANGUAGE_REGION_CODE) => {
    const inputTime = (0, parser_1.parseWithTimezone)(input, fromTimezone);
    const convertedTime = inputTime.utc();
    // Format the local time using the selected format
    return (0, formatter_1.formatDateTime)(convertedTime, format, languageRegionCode);
};
exports.convertTimestampFromTimezoneToUtc = convertTimestampFromTimezoneToUtc;
/**
 * Converts a UTC timestamp to the local time of the system's timezone and formats it as a string.
 *
 * @param {DateInput} input - The UTC timestamp to convert to the local time.
 * @param {DateTimeFormats} [format=DateTimeFormats.LLLL] - The format to use for formatting the local time. Default is the LLLL.
 * @param {LanguageRegionCodes} [languageRegionCode=LanguageRegionCodes.EN_US] - The language and region code to use for localization. Default is the EN_US.
 * @returns {string} The formatted local time in the system's timezone.
 */
const convertUtcTimestampToLocalTime = (input, format = types_1.DEFAULT_FORMAT, languageRegionCode = types_1.DEFAULT_LANGUAGE_REGION_CODE) => {
    const toTimezone = dayjs_1.default.tz.guess();
    return (0, exports.convertUtcTimestampToTimezone)(input, toTimezone, format, languageRegionCode);
};
exports.convertUtcTimestampToLocalTime = convertUtcTimestampToLocalTime;
/**
 * Converts a local timestamp from the system's timezone to UTC and formats it as a string.
 *
 * @param {DateInput} input - The local timestamp to convert to UTC.
 * @param {DateTimeFormats} [format=DateTimeFormats.LLLL] - The format to use for formatting the UTC time. Default is the LLLL.
 * @param {LanguageRegionCodes} [languageRegionCode=LanguageRegionCodes.EN_US] - The language and region code to use for localization. Default is the EN_US.
 * @returns {string} The formatted UTC time.
 */
const convertLocalTimestampToUtc = (input, format = types_1.DEFAULT_FORMAT, languageRegionCode = types_1.DEFAULT_LANGUAGE_REGION_CODE) => {
    const fromTimezone = dayjs_1.default.tz.guess();
    return (0, exports.convertTimestampFromTimezoneToUtc)(input, fromTimezone, format, languageRegionCode);
};
exports.convertLocalTimestampToUtc = convertLocalTimestampToUtc;
/**
 * Converts a timestamp from one timezone to another and formats it as a string.
 *
 * @param {DateInput} input - The input date to convert from the `fromTimezone` to the `toTimezone`.
 * @param {string} fromTimezone - The timezone from which the timestamp should be converted.
 * @param {string} toTimezone - The timezone to which the timestamp should be converted.
 * @param {DateTimeFormats} [format=DateTimeFormats.LLLL] - The format to use for formatting the converted time. Default is the LLLL.
 * @param {LanguageRegionCodes} [languageRegionCode=LanguageRegionCodes.EN_US] - The language and region code to use for localization. Default is the EN_US.
 * @returns {string} The formatted time in the `toTimezone`.
 */
const convertTimestampFromTimezoneToTimezone = (input, fromTimezone, toTimezone, format = types_1.DEFAULT_FORMAT, languageRegionCode = types_1.DEFAULT_LANGUAGE_REGION_CODE) => {
    const inputTime = (0, parser_1.parseWithTimezone)(input, fromTimezone);
    return (0, exports.convertUtcTimestampToTimezone)(inputTime.toISOString(), toTimezone, format, languageRegionCode);
};
exports.convertTimestampFromTimezoneToTimezone = convertTimestampFromTimezoneToTimezone;
/**
 * Converts a given time in 24-hour format to 12-hour format with AM/PM.
 *
 * @param hour - The hour value in 24-hour format.
 * @param minutes - The minutes value.
 * @returns The time in 12-hour format with AM/PM.
 */
const convertTime24ToAmPm = (hour, minutes) => {
    // Create a Day.js object with the specified hour and minutes
    const time = (0, dayjs_1.default)().hour(hour).minute(minutes);
    // Format the time to 12-hour format with AM/PM
    return time.format(types_1.DateTimeFormats.TIME_12_HOUR);
};
exports.convertTime24ToAmPm = convertTime24ToAmPm;
/**
 * convert from bond days to isoDays
 * where bond days start as Monday = 2 and ends on Sunday = 8
 * isoDays starts are Monday = 1 and Sunday = 7
 * @param day
 */
function bondDaysToIsoDays(day) {
    return day - 1;
}
exports.bondDaysToIsoDays = bondDaysToIsoDays;
//# sourceMappingURL=converter.js.map